import {useState} from 'react';
import {
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  LayoutGrid,
  Plus,
  RotateCcw,
  Trash2,
} from 'lucide-react';
import {Button} from '@common-web/components/ui/button';
import {Badge} from '@common-web/components/ui/badge';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {Checkbox} from '@common-web/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@common-web/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {
  ACCOUNTS_SUB_STAGES,
  ACCOUNTS_VIEW_TABLE_COLUMNS,
  CANONICAL_SUB_STAGE_PHASE,
  DEFAULT_PHASE_LABELS,
  DEFAULT_SUB_STAGE_LABELS,
  LIFECYCLE_PHASES,
  accountsViewConfigSchema,
  internalAccountsViewConfigSchema,
  resolveAccountsViewConfig,
  type AccountsSubStageKey,
  type AccountsViewConfig,
  type AccountsViewStage,
  type AccountsViewSubStage,
  type AccountsViewTableColumnKey,
  type LifecyclePhaseKey,
} from '@common/accountsView';

// A row needs an identity, so the Company column can't be turned off.
const LOCKED_COLUMN: AccountsViewTableColumnKey = 'company';

function newKey(prefix: string): string {
  const rand =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID().slice(0, 8)
      : Math.random().toString(36).slice(2, 10);
  return `${prefix}-${rand}`;
}

function cloneConfig(config: AccountsViewConfig): AccountsViewConfig {
  return {
    stages: config.stages.map((s) => ({
      ...s,
      subStages: s.subStages.map((ss) => ({...ss})),
    })),
    tableColumns: config.tableColumns ? [...config.tableColumns] : undefined,
  };
}

function moveItem<T>(items: T[], index: number, delta: number): T[] {
  const target = index + delta;
  if (target < 0 || target >= items.length) return items;
  const next = [...items];
  const [item] = next.splice(index, 1);
  next.splice(target, 0, item);
  return next;
}

const SUB_STAGE_OPTIONS: Array<{value: AccountsSubStageKey; label: string}> =
  ACCOUNTS_SUB_STAGES.map((ss) => ({
    value: ss,
    label: `${DEFAULT_PHASE_LABELS[CANONICAL_SUB_STAGE_PHASE[ss]]} · ${DEFAULT_SUB_STAGE_LABELS[ss]}`,
  }));

type AccountsViewConfigSectionProps = {
  /** Current persisted config (undefined = default pipeline). */
  value: AccountsViewConfig | null | undefined;
  disabled?: boolean;
  isPending?: boolean;
  /** Persist a full custom config. */
  onSave: (config: AccountsViewConfig) => void;
  /** Clear the custom config and revert to the default pipeline. */
  onReset: () => void;
  /** Heading copy varies between the per-client and org-wide consoles. */
  scopeLabel?: string;
  /**
   * Which schema validates on save. 'client' (default) requires every
   * lifecycle phase to be represented; 'internal' allows a subset so the
   * internal portal can scope to Implementation-forward.
   */
  scope?: 'client' | 'internal';
};

/**
 * Collapsible summary + "Customize" dialog for the Accounts pipeline. Used by
 * both the per-tenant (/clients) and org-wide (/internal-features) consoles.
 */
export function AccountsViewConfigSection({
  value,
  disabled,
  isPending,
  onSave,
  onReset,
  scopeLabel,
  scope = 'client',
}: AccountsViewConfigSectionProps) {
  const [open, setOpen] = useState(false);
  const isCustomized = !!(value && value.stages && value.stages.length > 0);
  const active = resolveAccountsViewConfig(value, scope);

  return (
    <div className='border border-gray-200 rounded-lg bg-white p-4 space-y-3'>
      <div className='flex items-start justify-between gap-3'>
        <div className='flex items-start gap-3'>
          <div className='p-2 bg-brand-blue/5 rounded-md'>
            <LayoutGrid className='h-4 w-4 text-brand-blue' />
          </div>
          <div className='space-y-1'>
            <div className='text-sm font-semibold text-brand-navy'>Accounts View Pipeline</div>
            <p className='text-sm text-brand-gray'>
              {scopeLabel ??
                'Customize the kanban stages, sub-stages, column count, and table columns for the Accounts view.'}{' '}
              Each stage and sub-stage maps to a system phase so RFC/MSA/CBS automation keeps
              working. Defaults to the standard 5-stage pipeline.
            </p>
          </div>
        </div>
        <Badge
          className={
            isCustomized
              ? 'bg-emerald/10 text-emerald border-emerald/20 shrink-0'
              : 'bg-brand-gray/10 text-brand-gray border-brand-gray/20 shrink-0'
          }
        >
          {isCustomized ? 'Custom' : 'Default'}
        </Badge>
      </div>

      <div className='flex items-center gap-3 text-xs text-brand-gray'>
        <span>
          {active.stages.length} stage{active.stages.length === 1 ? '' : 's'} ·{' '}
          {active.stages.reduce((n, s) => n + s.subStages.length, 0)} sub-stages
        </span>
      </div>

      <div className='flex items-center gap-2'>
        <Button
          type='button'
          variant='outline'
          size='sm'
          disabled={disabled}
          onClick={() => setOpen(true)}
        >
          Customize
        </Button>
        {isCustomized && (
          <Button
            type='button'
            variant='ghost'
            size='sm'
            disabled={disabled || isPending}
            onClick={onReset}
            className='gap-1 text-brand-gray'
          >
            <RotateCcw className='h-3.5 w-3.5' />
            Reset to default
          </Button>
        )}
      </div>

      {open && (
        <AccountsViewConfigDialog
          initial={value}
          isPending={isPending}
          scope={scope}
          onClose={() => setOpen(false)}
          onSave={(config) => {
            onSave(config);
            setOpen(false);
          }}
        />
      )}
    </div>
  );
}

function AccountsViewConfigDialog({
  initial,
  isPending,
  scope = 'client',
  onClose,
  onSave,
}: {
  initial: AccountsViewConfig | null | undefined;
  isPending?: boolean;
  scope?: 'client' | 'internal';
  onClose: () => void;
  onSave: (config: AccountsViewConfig) => void;
}) {
  const [draft, setDraft] = useState<AccountsViewConfig>(() =>
    cloneConfig(resolveAccountsViewConfig(initial, scope)),
  );
  const [errors, setErrors] = useState<string[]>([]);

  const visibleColumns = new Set<AccountsViewTableColumnKey>(
    draft.tableColumns ?? ACCOUNTS_VIEW_TABLE_COLUMNS.map((c) => c.key),
  );

  const updateStages = (stages: AccountsViewStage[]) => setDraft((d) => ({...d, stages}));

  const updateStage = (index: number, patch: Partial<AccountsViewStage>) =>
    updateStages(draft.stages.map((s, i) => (i === index ? {...s, ...patch} : s)));

  const updateSubStage = (
    stageIndex: number,
    subIndex: number,
    patch: Partial<AccountsViewSubStage>,
  ) =>
    updateStage(stageIndex, {
      subStages: draft.stages[stageIndex].subStages.map((ss, i) =>
        i === subIndex ? {...ss, ...patch} : ss,
      ),
    });

  const addStage = () => {
    const stage: AccountsViewStage = {
      key: newKey('stage'),
      label: 'New Stage',
      systemPhase: 'LEAD',
      subStages: [{key: newKey('sub'), label: 'New Sub-stage', systemSubStage: 'NEW_LEAD'}],
    };
    updateStages([...draft.stages, stage]);
  };

  const removeStage = (index: number) => updateStages(draft.stages.filter((_, i) => i !== index));

  const addSubStage = (stageIndex: number) => {
    const stage = draft.stages[stageIndex];
    // Seed the new sub-stage with a canonical value that belongs to the stage's
    // system phase so the default mapping is sensible.
    const fallback: AccountsSubStageKey =
      ACCOUNTS_SUB_STAGES.find((ss) => CANONICAL_SUB_STAGE_PHASE[ss] === stage.systemPhase) ??
      'NEW_LEAD';
    updateStage(stageIndex, {
      subStages: [
        ...stage.subStages,
        {key: newKey('sub'), label: 'New Sub-stage', systemSubStage: fallback},
      ],
    });
  };

  const removeSubStage = (stageIndex: number, subIndex: number) =>
    updateStage(stageIndex, {
      subStages: draft.stages[stageIndex].subStages.filter((_, i) => i !== subIndex),
    });

  const toggleColumn = (key: AccountsViewTableColumnKey, checked: boolean) => {
    const next = new Set(visibleColumns);
    if (checked) next.add(key);
    else next.delete(key);
    // Persist in catalog order; always include the locked column.
    next.add(LOCKED_COLUMN);
    setDraft((d) => ({
      ...d,
      tableColumns: ACCOUNTS_VIEW_TABLE_COLUMNS.map((c) => c.key).filter((k) => next.has(k)),
    }));
  };

  const handleSave = () => {
    const schema =
      scope === 'internal' ? internalAccountsViewConfigSchema : accountsViewConfigSchema;
    const result = schema.safeParse(draft);
    if (!result.success) {
      setErrors(result.error.issues.map((i) => i.message));
      return;
    }
    setErrors([]);
    onSave(result.data as AccountsViewConfig);
  };

  const handleResetDraft = () => {
    setDraft(cloneConfig(resolveAccountsViewConfig(undefined, scope)));
    setErrors([]);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className='max-w-3xl max-h-[85vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>Customize Accounts View</DialogTitle>
          <DialogDescription>
            Stages render as kanban columns (the number of stages is the column count). Map each
            stage to a system phase and each sub-stage to a system sub-stage so automation keeps
            working.
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-4 py-2'>
          {draft.stages.map((stage, stageIndex) => (
            <div
              key={stage.key}
              className={`border border-gray-200 rounded-lg p-4 space-y-3 ${
                stage.hidden ? 'opacity-60' : ''
              }`}
            >
              <div className='flex items-end gap-2'>
                <div className='flex-1 space-y-1.5'>
                  <Label htmlFor={`stage-label-${stage.key}`}>Stage name</Label>
                  <Input
                    id={`stage-label-${stage.key}`}
                    value={stage.label}
                    onChange={(e) => updateStage(stageIndex, {label: e.target.value})}
                  />
                  {stage.hidden ? (
                    <Badge className='bg-brand-gray/10 text-brand-gray border-brand-gray/20'>
                      Hidden
                    </Badge>
                  ) : null}
                </div>
                <div className='w-48 space-y-1.5'>
                  <Label>System phase</Label>
                  <Select
                    value={stage.systemPhase}
                    onValueChange={(v) =>
                      updateStage(stageIndex, {systemPhase: v as LifecyclePhaseKey})
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LIFECYCLE_PHASES.map((p) => (
                        <SelectItem key={p} value={p}>
                          {DEFAULT_PHASE_LABELS[p]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className='flex items-center gap-1 pb-0.5'>
                  <Button
                    type='button'
                    variant='ghost'
                    size='icon'
                    className='h-9 w-9'
                    title={stage.hidden ? 'Show stage' : 'Hide stage'}
                    onClick={() => updateStage(stageIndex, {hidden: !stage.hidden})}
                  >
                    {stage.hidden ? (
                      <EyeOff className='h-4 w-4 text-brand-gray' />
                    ) : (
                      <Eye className='h-4 w-4' />
                    )}
                  </Button>
                  <Button
                    type='button'
                    variant='outline'
                    size='icon'
                    className='h-9 w-9'
                    disabled={stageIndex === 0}
                    onClick={() => updateStages(moveItem(draft.stages, stageIndex, -1))}
                  >
                    <ChevronUp className='h-4 w-4' />
                  </Button>
                  <Button
                    type='button'
                    variant='outline'
                    size='icon'
                    className='h-9 w-9'
                    disabled={stageIndex === draft.stages.length - 1}
                    onClick={() => updateStages(moveItem(draft.stages, stageIndex, 1))}
                  >
                    <ChevronDown className='h-4 w-4' />
                  </Button>
                  <Button
                    type='button'
                    variant='ghost'
                    size='icon'
                    className='h-9 w-9 text-red-500 hover:text-red-600'
                    disabled={draft.stages.length === 1}
                    onClick={() => removeStage(stageIndex)}
                  >
                    <Trash2 className='h-4 w-4' />
                  </Button>
                </div>
              </div>

              <div className='space-y-2 pl-4 border-l-2 border-gray-100'>
                {stage.subStages.map((sub, subIndex) => (
                  <div key={sub.key} className='flex items-end gap-2'>
                    <div className='flex-1 space-y-1.5'>
                      <Label htmlFor={`sub-label-${sub.key}`} className='text-xs'>
                        Sub-stage name
                      </Label>
                      <Input
                        id={`sub-label-${sub.key}`}
                        value={sub.label}
                        onChange={(e) =>
                          updateSubStage(stageIndex, subIndex, {label: e.target.value})
                        }
                      />
                    </div>
                    <div className='w-56 space-y-1.5'>
                      <Label className='text-xs'>System sub-stage</Label>
                      <Select
                        value={sub.systemSubStage}
                        onValueChange={(v) =>
                          updateSubStage(stageIndex, subIndex, {
                            systemSubStage: v as AccountsSubStageKey,
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {SUB_STAGE_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className='flex items-center gap-1 pb-0.5'>
                      <Button
                        type='button'
                        variant='outline'
                        size='icon'
                        className='h-9 w-9'
                        disabled={subIndex === 0}
                        onClick={() =>
                          updateStage(stageIndex, {
                            subStages: moveItem(stage.subStages, subIndex, -1),
                          })
                        }
                      >
                        <ChevronUp className='h-4 w-4' />
                      </Button>
                      <Button
                        type='button'
                        variant='outline'
                        size='icon'
                        className='h-9 w-9'
                        disabled={subIndex === stage.subStages.length - 1}
                        onClick={() =>
                          updateStage(stageIndex, {
                            subStages: moveItem(stage.subStages, subIndex, 1),
                          })
                        }
                      >
                        <ChevronDown className='h-4 w-4' />
                      </Button>
                      <Button
                        type='button'
                        variant='ghost'
                        size='icon'
                        className='h-9 w-9 text-red-500 hover:text-red-600'
                        disabled={stage.subStages.length === 1}
                        onClick={() => removeSubStage(stageIndex, subIndex)}
                      >
                        <Trash2 className='h-4 w-4' />
                      </Button>
                    </div>
                  </div>
                ))}
                <Button
                  type='button'
                  variant='ghost'
                  size='sm'
                  onClick={() => addSubStage(stageIndex)}
                  className='gap-1 text-brand-blue'
                >
                  <Plus className='h-3.5 w-3.5' />
                  Add sub-stage
                </Button>
              </div>
            </div>
          ))}

          <Button type='button' variant='outline' size='sm' onClick={addStage} className='gap-1'>
            <Plus className='h-4 w-4' />
            Add stage
          </Button>

          <div className='border border-gray-200 rounded-lg p-4 space-y-3'>
            <div className='text-sm font-semibold text-brand-navy'>Table columns</div>
            <p className='text-xs text-brand-gray'>
              Choose which columns appear in the Accounts table view. Company is always shown.
            </p>
            <div className='grid grid-cols-2 gap-2'>
              {ACCOUNTS_VIEW_TABLE_COLUMNS.map((col) => {
                const locked = col.key === LOCKED_COLUMN;
                return (
                  <label
                    key={col.key}
                    className='flex items-center gap-2 text-sm text-brand-navy cursor-pointer'
                  >
                    <Checkbox
                      checked={visibleColumns.has(col.key)}
                      disabled={locked}
                      onCheckedChange={(c) => toggleColumn(col.key, c === true)}
                    />
                    {col.label}
                    {locked && <span className='text-xs text-brand-gray'>(required)</span>}
                  </label>
                );
              })}
            </div>
          </div>

          {errors.length > 0 && (
            <div className='border border-red-200 bg-red-50 rounded-lg p-3 space-y-1'>
              {errors.map((msg, i) => (
                <div key={i} className='text-sm text-red-600'>
                  {msg}
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter className='gap-2 sm:justify-between'>
          <Button type='button' variant='ghost' onClick={handleResetDraft} className='gap-1'>
            <RotateCcw className='h-3.5 w-3.5' />
            Reset to default
          </Button>
          <div className='flex items-center gap-2'>
            <Button type='button' variant='outline' onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type='button' onClick={handleSave} disabled={isPending}>
              {isPending ? 'Saving…' : 'Save pipeline'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
